import { amountToTotalNet } from './amount-to-total-net.function';

/**
 * Calculates a pricing item’s total net amount.
 * @param pricingItem The item to price:
 * 	- `units`         – how many units
 * 	- `unitPrice`     – price per unit (net or gross)
 * 	- `unitTaxRate`   – tax rate as a percentage (e.g. 19 for 19%)
 * 	- `isTaxIncluded` – whether `unitPrice` already includes tax (gross). Defaults to false.
 * @returns The total net amount for all units.
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: true,
 * };
 * const totalNet = unitPricingToTotalNet(pricingItem);
 * console.log(totalNet); // Output: 336.1344537815126
 * ```
 * @example
 * ```ts
 * const pricingItem = {
 *   units: 4,
 *   unitPrice: 100,
 *   unitTaxRate: 19,
 *   isTaxIncluded: false,
 * };
 * const totalNet = unitPricingToTotalNet(pricingItem);
 * console.log(totalNet); // Output: 400
 * ```
 */
export function unitPricingToTotalNet(pricingItem: {
	units?: number;
	unitPrice?: number;
	unitTaxRate?: number;
	isTaxIncluded?: boolean;
}): number {
	if (!pricingItem) {
		return 0;
	}

	const units =
		pricingItem.units == null ||
		isNaN(pricingItem.units) ||
		pricingItem.units < 0
			? 0
			: pricingItem.units;
	const unitPrice =
		pricingItem.unitPrice == null || isNaN(pricingItem.unitPrice)
			? 0
			: pricingItem.unitPrice;
	const unitTaxRate =
		isNaN(pricingItem.unitTaxRate ?? NaN) || (pricingItem.unitTaxRate ?? 0) < 0
			? 0
			: pricingItem.unitTaxRate;

	// Calculate total net for all units
	return (
		units * amountToTotalNet(unitPrice, unitTaxRate, pricingItem.isTaxIncluded)
	);
}
